/*
  Sound utilities for Tadaber Workforce.
  Generates airline-style chimes via Web Audio API (no mp3 files needed, no CORS, no network).
  All playback respects the user's sound preferences saved in localStorage under `soundSettings`.
*/

const STORAGE_KEY = 'soundSettings';
let audioCtx = null;
let welcomePlayedThisSession = false;

/* ============================================================
   PREFERENCES
   ============================================================ */
export const getSoundSettings = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { welcome_sound: false, notification_sound: false };
    const parsed = JSON.parse(raw);
    return {
      welcome_sound: !!parsed.welcome_sound,
      notification_sound: !!parsed.notification_sound,
    };
  } catch {
    return { welcome_sound: false, notification_sound: false };
  }
};

export const setSoundSettings = (settings) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
};

/* ============================================================
   INTERNAL – get/create AudioContext (lazy, browser-safe)
   ============================================================ */
const getAudioContext = () => {
  if (audioCtx) return audioCtx;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  try {
    audioCtx = new Ctx();
    return audioCtx;
  } catch {
    return null;
  }
};

// Browsers block autoplay until a user gesture – resume on first gesture
const ensureResumed = async (ctx) => {
  if (ctx && ctx.state === 'suspended') {
    try { await ctx.resume(); } catch { /* ignore */ }
  }
};

/**
 * Play a sequence of simple sine tones to create a pleasant chime.
 * notes = array of { freq: Hz, at: secondsFromStart, dur: seconds, gain: 0..1 }
 */
const playNotes = async (notes) => {
  const ctx = getAudioContext();
  if (!ctx) return;
  await ensureResumed(ctx);
  const now = ctx.currentTime;
  const master = ctx.createGain();
  master.gain.value = 0.22; // overall volume
  master.connect(ctx.destination);

  notes.forEach((n) => {
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = n.freq;
    // simple attack-decay envelope
    const start = now + (n.at || 0);
    const end = start + (n.dur || 0.25);
    g.gain.setValueAtTime(0.0001, start);
    g.gain.exponentialRampToValueAtTime(n.gain || 0.9, start + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, end);
    osc.connect(g);
    g.connect(master);
    osc.start(start);
    osc.stop(end + 0.05);
  });
};

/* ============================================================
   PUBLIC API
   ============================================================ */

/** Airline boarding-style welcome chime: three ascending soft tones.
 *  Plays ONCE per browser session even if called multiple times. */
export const playWelcomeChime = async ({ force = false } = {}) => {
  const { welcome_sound } = getSoundSettings();
  if (!welcome_sound) return;
  if (welcomePlayedThisSession && !force) return;
  welcomePlayedThisSession = true;
  await playNotes([
    { freq: 659.25, at: 0.00, dur: 0.32 },  // E5
    { freq: 783.99, at: 0.22, dur: 0.32 },  // G5
    { freq: 1046.5, at: 0.50, dur: 0.45 },  // C6
  ]);
};

/** Gentle two-tone airport "ding-dong" for a new notification. */
export const playNotificationChime = async () => {
  const { notification_sound } = getSoundSettings();
  if (!notification_sound) return;
  await playNotes([
    { freq: 1046.5, at: 0.00, dur: 0.22 },  // C6
    { freq: 783.99, at: 0.18, dur: 0.28 },  // G5
  ]);
};

/** Force-play for "test sound" buttons – ignores preferences. */
export const playWelcomeChimePreview = () =>
  playNotes([
    { freq: 659.25, at: 0.00, dur: 0.32 },
    { freq: 783.99, at: 0.22, dur: 0.32 },
    { freq: 1046.5, at: 0.50, dur: 0.45 },
  ]);

export const playNotificationChimePreview = () =>
  playNotes([
    { freq: 1046.5, at: 0.00, dur: 0.22 },
    { freq: 783.99, at: 0.18, dur: 0.28 },
  ]);
